import { useState, type FormEvent } from 'react';
import { BailaLogo } from '@/components/BailaLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';

/**
 * LoginPage — flujo de magic link via Stytch B2B (BFMWEB-4).
 * El usuario introduce su email y le enviamos un enlace de un solo
 * uso que aterriza en /auth/callback.
 */
export default function LoginPage() {
  const { signInWithEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('sending');
    setError(null);
    try {
      await signInWithEmail(email.trim());
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'No se ha podido enviar el enlace.');
    }
  }

  return (
    <div className="w-full max-w-sm space-y-8 rounded-lg border bg-card p-10 shadow-sm">
      <div className="space-y-4 text-center">
        <div className="flex items-center justify-center text-black">
          <BailaLogo width={188} height={36} />
        </div>
        <p className="text-sm text-muted-foreground">
          Te enviamos un enlace de acceso a tu correo.
        </p>
      </div>

      {status === 'sent' ? (
        <div className="space-y-4 text-center">
          <p className="text-sm">
            Revisa <span className="font-medium">{email}</span>. Pincha en el
            enlace para entrar al panel.
          </p>
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => {
              setStatus('idle');
              setEmail('');
            }}
          >
            Usar otro correo
          </Button>
        </div>
      ) : (
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="tu@baila.fm"
              required
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              disabled={status === 'sending'}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={status === 'sending'}>
            {status === 'sending' ? 'Enviando…' : 'Enviar enlace mágico'}
          </Button>
        </form>
      )}
    </div>
  );
}
